/**
 * Shared types for campaign media adjustments (crop + text overlay).
 * These are stored in the backend as part of campaign_config.media_adjustments.
 * All values are resolution-independent fractions (0–1).
 */

/** Crop region expressed as fractions of the original image dimensions */
export interface CropParams {
  x_frac: number;  // left edge / original width
  y_frac: number;  // top edge / original height
  w_frac: number;  // crop width / original width
  h_frac: number;  // crop height / original height
}

/**
 * Text layer stored with all positions normalized as fractions of the crop viewport.
 * x_frac / y_frac are the top-left of the text box.
 * w_frac is the box width.  fontSize_vw is font size / crop-viewport width.
 */
export interface StoredTextLayer {
  id: string;
  text: string;
  x_frac: number;
  y_frac: number;
  w_frac: number;
  fontSize_vw: number;
  fontFamily: string;
  fill: string;
  fontStyle: string;
  align: 'left' | 'center' | 'right';
  opacity: number;
  rotation: number;
  effect: string;
  effectParams: Record<string, unknown>;
  letterSpacing_vw?: number;  // fraction of viewport width
  lineHeight?: number;        // multiplier (unitless, stored as-is)
  bgShape?: 'none' | 'rect' | 'pill';
  bgColor?: string;
  bgOpacity?: number;
  bgPadding_vw?: number;      // fraction of viewport width
}

/** All adjustments for one filename + one platform */
export interface PlatformAdjustment {
  crop: CropParams;
  textLayers: StoredTextLayer[];
  /** Target output dimensions — backend resizes the cropped image to these exact pixels */
  targetWidth?: number;
  targetHeight?: number;
}

/**
 * Full media_adjustments map stored inside campaign_config.
 * Shape: { [original_filename]: { [platform]: PlatformAdjustment | null } }
 * null means "use original image, no adjustments".
 */
export type MediaAdjustments = Record<string, Record<string, PlatformAdjustment | null>>;

/**
 * Compute a center-crop CropParams that fills the target aspect ratio.
 * Used by both UnifiedImageEditor (interactive) and auto-crop (on proposal load).
 */
export function computeCenterCrop(
  naturalW: number,
  naturalH: number,
  targetW: number,
  targetH: number,
): CropParams {
  const targetAR = targetW / targetH;
  const imageAR = naturalW / naturalH;
  let w_frac: number, h_frac: number;
  if (imageAR > targetAR) {
    // Image is wider than target — crop sides, full height
    h_frac = 1.0;
    w_frac = targetAR / imageAR;
  } else {
    // Image is taller than target — crop top/bottom, full width
    w_frac = 1.0;
    h_frac = imageAR / targetAR;
  }
  return {
    x_frac: (1 - w_frac) / 2,
    y_frac: (1 - h_frac) / 2,
    w_frac,
    h_frac,
  };
}
